import { GridColDef } from '@mui/x-data-grid';
import { GridInitialStateCommunity } from '@mui/x-data-grid/models/gridStateCommunity';
import dayjs from 'dayjs';
import { default as utc } from 'dayjs/plugin/utc';
import { FC, PropsWithChildren, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from 'components';
import DataTable, { DataTableColumn } from 'components/tables/data-table';
import { useAuth } from 'contexts';
import { SelectedLocationContext } from 'contexts/selected-location';
import { fetchPendingInvoices } from 'services';
import { downloadCsv } from 'services/download-csv';
import { Customer, InvoiceStatus, InvoiceSummary, ListContextType } from 'types';
import './hold-card.scss';

dayjs.extend(utc);

const DollarFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

const columnDefs: GridColDef<InvoiceSummary>[] = [
  {
    field: 'customer',
    headerName: 'Account',
    valueGetter: ({ value }) => (value as Customer).code || (value as Customer).name.toUpperCase(),
    width: 120,
    cellClassName: ''
  },
  {
    field: 'fromDate',
    headerName: 'From',
    type: 'date',
    valueFormatter: ({ value }) => (!value ? null : dayjs(value).utc().format('M/D/YYYY'))
  },
  {
    field: 'toDate',
    headerName: 'To',
    type: 'date',
    valueFormatter: ({ value }) => (!value ? null : dayjs(value).utc().format('M/D/YYYY'))
  },
  {
    field: 'status',
    width: 125,
    headerName: 'Status',
    renderCell: ({ value, row }) => {
      switch (value as InvoiceStatus) {
        case InvoiceStatus.NEEDS_REVIEW:
          return <></>;
        case InvoiceStatus.APPROVED:
          return <Badge text="Approved" backgroundColor="var(--green)" />;
        case InvoiceStatus.HOLD:
          return (
            <>
              <span className="overflow">
                <Badge text="Hold" backgroundColor="var(--red)" />
              </span>
              <span className="tooltip">{row.statusReason}</span>
            </>
          );
      }
    },
    type: 'singleSelect',
    valueOptions: [InvoiceStatus.NEEDS_REVIEW, InvoiceStatus.APPROVED, InvoiceStatus.HOLD]
  },
  {
    field: 'totalCharge',
    headerName: 'Total',
    filterable: false,
    width: 110,
    valueGetter: ({ row }) => DollarFormatter.format(row.totalCharge),
    cellClassName: 'font-weight-bold'
  },
  {
    field: 'chargedDays',
    headerName: 'Charged Days',
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    width: 150,
    filterable: false,
    valueGetter: ({ row }) => row.chargedDays
  },
  {
    field: 'units',
    headerName: 'Units',
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    valueGetter: ({ row }) => row.unitCount
  },
  {
    field: 'emptyStorage',
    headerName: 'Empty Storage',
    width: 175,
    filterable: false,
    valueGetter: ({ row }) => DollarFormatter.format(row.emptyStorageCharge)
  },
  {
    field: 'loadedStorage',
    headerName: 'Loaded Storage',
    width: 200,
    filterable: false,
    valueGetter: ({ row }) => DollarFormatter.format(row.loadedStorageCharge)
  },
  {
    field: 'emptyGateIn',
    headerName: 'Empty Gate In',
    width: 165,
    filterable: false,
    valueGetter: ({ row }) => DollarFormatter.format(row.emptyGateInCharge)
  },
  {
    field: 'loadedGateIn',
    headerName: 'Loaded Gate In',
    width: 170,
    filterable: false,
    valueGetter: ({ row }) => DollarFormatter.format(row.loadedGateInCharge)
  },
  {
    field: 'emptyGateOut',
    headerName: 'Empty Gate Out',
    width: 175,
    filterable: false,
    valueGetter: ({ row }) => DollarFormatter.format(row.emptyGateOutCharge)
  },
  {
    field: 'loadedGateOut',
    headerName: 'Loaded Gate Out',
    width: 200,
    filterable: false,
    valueGetter: ({ row }) => DollarFormatter.format(row.loadedGateOutCharge)
  },
  {
    field: 'credit',
    headerName: 'Credit',
    width: 100,
    filterable: false,
    valueGetter: ({ row }) => DollarFormatter.format(row.credit)
  }
];

const dataColumnDefs: DataTableColumn[] = [
  {
    field: 'customer',
    sortField: 'customer.name',
    label: 'Account',
    getValue: (row: InvoiceSummary) => row.customer.code || row.customer.name.toUpperCase(),
    width: 120,
    className: 'link font-weight-regular'
  },
  {
    field: 'fromDate',
    label: 'From',
    type: 'zoned',
    width: 100,
    getValueLabel: (v: Date) => (!!v ? dayjs.utc(v).format('M/D/YY LT') : null)
  },
  {
    field: 'toDate',
    label: 'To',
    type: 'zoned',
    width: 100,
    getValueLabel: (v: Date) => (!!v ? dayjs.utc(v).format('M/D/YY LT') : null)
  },
  {
    field: 'status',
    width: 125,
    label: 'Status',
    renderCell: (value: InvoiceStatus, row: InvoiceSummary) => {
      switch (value) {
        case InvoiceStatus.NEEDS_REVIEW:
          return <></>;
        case InvoiceStatus.APPROVED:
          return <Badge text="Approved" backgroundColor="var(--green)" />;
        case InvoiceStatus.HOLD:
          return (
            <>
              <span className="overflow">
                <Badge text="Hold" backgroundColor="var(--red)" />
              </span>
              <span className="tooltip">{row.statusReason}</span>
            </>
          );
      }
    },
    type: 'singleSelect',
    valueOptions: [InvoiceStatus.NEEDS_REVIEW, InvoiceStatus.APPROVED, InvoiceStatus.HOLD]
  },
  {
    field: 'totalCharge',
    label: 'Total',
    filterable: false,
    sortable: false,
    skipQuickSearch: true,
    width: 110,
    getValue: (row: InvoiceSummary) => DollarFormatter.format(row.totalCharge),
    className: 'font-weight-bold'
  },
  {
    field: 'chargedDays',
    label: 'Charged Days',
    type: 'number',
    width: 150,
    filterable: false,
    sortable: false,
    skipQuickSearch: true,
    getValue: (row: InvoiceSummary) => row.chargedDays
  },
  {
    field: 'units',
    label: 'Units',
    type: 'number',
    width: 100,
    filterable: false,
    sortable: false,
    skipQuickSearch: true,
    getValue: (row: InvoiceSummary) => row.unitCount
  },
  {
    field: 'emptyStorage',
    label: 'Empty Storage',
    width: 175,
    filterable: false,
    sortable: false,
    skipQuickSearch: true,
    getValue: (row: InvoiceSummary) => DollarFormatter.format(row.emptyStorageCharge)
  },
  {
    field: 'loadedStorage',
    label: 'Loaded Storage',
    width: 200,
    filterable: false,
    sortable: false,
    skipQuickSearch: true,
    getValue: (row: InvoiceSummary) => DollarFormatter.format(row.loadedStorageCharge)
  },
  {
    field: 'emptyGateIn',
    label: 'Empty Gate In',
    width: 165,
    filterable: false,
    sortable: false,
    skipQuickSearch: true,
    getValue: (row: InvoiceSummary) => DollarFormatter.format(row.emptyGateInCharge)
  },
  {
    field: 'loadedGateIn',
    label: 'Loaded Gate In',
    width: 170,
    filterable: false,
    sortable: false,
    skipQuickSearch: true,
    getValue: (row: InvoiceSummary) => DollarFormatter.format(row.loadedGateInCharge)
  },
  {
    field: 'emptyGateOut',
    label: 'Empty Gate Out',
    width: 175,
    filterable: false,
    sortable: false,
    skipQuickSearch: true,
    getValue: (row: InvoiceSummary) => DollarFormatter.format(row.emptyGateOutCharge)
  },
  {
    field: 'loadedGateOut',
    label: 'Loaded Gate Out',
    width: 200,
    filterable: false,
    sortable: false,
    skipQuickSearch: true,
    getValue: (row: InvoiceSummary) => DollarFormatter.format(row.loadedGateOutCharge)
  },
  {
    field: 'credit',
    label: 'Credit',
    width: 100,
    filterable: false,
    sortable: false,
    skipQuickSearch: true,
    getValue: (row: InvoiceSummary) => DollarFormatter.format(row.credit)
  }
];

const defaultVisibility: {
  [P in keyof Partial<InvoiceSummary>]: boolean;
} = {
  customer: true
};

const PendingInvoiceListContext = createContext<ListContextType<InvoiceSummary>>({
  columnDefs: columnDefs,
  listState: {},
  saveList: () => {}
});

export const PendingInvoiceListProvider: FC<PropsWithChildren<{ init?: GridInitialStateCommunity }>> = ({
  children,
  init
}) => {
  const [listState, saveList] = useState<GridInitialStateCommunity>({
    columns: { columnVisibilityModel: defaultVisibility },
    pagination: { paginationModel: { page: 0, pageSize: 100 } },
    sorting: { sortModel: [{ field: 'customer.code', sort: 'asc' }] },
    ...init
  });

  return (
    <PendingInvoiceListContext.Provider value={{ columnDefs, listState, saveList }}>
      {children}
    </PendingInvoiceListContext.Provider>
  );
};

export const PendingInvoiceList = (params: { rowSelectionChange: (rowSelection: InvoiceSummary[]) => void }) => {
  const navigate = useNavigate();
  const { selectedLocation } = useContext(SelectedLocationContext);
  const { userProfile } = useAuth();

  const initialColumns = useMemo(() => {
    return userProfile.columnPreferences.filter(cp => cp.table === 'INVOICE_PENDING').length > 0
      ? userProfile.columnPreferences
          .filter(cp => cp.table === 'INVOICE_PENDING')
          .map(({ column, width }) => {
            return { field: column, width };
          })
      : dataColumnDefs.map(c => {
          return { field: c.field, width: c.width };
        });
  }, [userProfile.columnPreferences]);

  const fetchData = useCallback(
    (params: {
      pageNum?: number;
      pageSize?: number;
      from?: Date;
      to?: Date;
      sortField?: string;
      sortDirection?: string;
      filterString?: string;
    }) => {
      return !!selectedLocation
        ? fetchPendingInvoices({ locationId: selectedLocation?.id, ...params })
        : Promise.resolve(null);
    },
    [selectedLocation]
  );

  return (
    <>
      <DataTable
        tableClass="t-height-invoice"
        fetchData={fetchData}
        fetchCsv={downloadCsv('invoices', { finalized: 'false' })}
        columns={dataColumnDefs}
        initialPage={{ pageNum: 0, pageSize: 100 }}
        onRowClick={(row: InvoiceSummary) => navigate(`/invoices/${row.id}`, { replace: true })}
        checkboxSelection
        onRowSelectionModelChange={params.rowSelectionChange}
        initialColumns={initialColumns}
        filterByLocation
        enableSaveColumns="INVOICE_PENDING"
        showColumnsCategorized
      />
    </>
  );
};
